/**
 * DayZ terrains use a square world with the origin in the south-west corner:
 * `x` runs east, `z` runs north, both in metres. Map images are north-up, so
 * screen Y is flipped relative to `z`.
 */
export type MapPreset = {
  id: string;
  name: string;
  /** Side length of the playable square, in metres. */
  size: number;
  missionFolder: string;
  /** Folder under public/vanilla with bundled vanilla data, when we ship it. */
  vanilla?: string;
};

export const MAP_PRESETS: MapPreset[] = [
  {
    id: 'chernarusplus',
    name: 'Chernarus+',
    size: 15360,
    missionFolder: 'dayzOffline.chernarusplus',
    vanilla: 'vanilla/chernarusplus',
  },
  { id: 'enoch', name: 'Livonia (Enoch)', size: 12800, missionFolder: 'dayzOffline.enoch' },
  { id: 'sakhal', name: 'Sakhal', size: 12800, missionFolder: 'dayzOffline.sakhal' },
  { id: 'custom', name: 'Custom size', size: 15360, missionFolder: 'dayzOffline.custom' },
];

export function presetById(id: string): MapPreset {
  return MAP_PRESETS.find((p) => p.id === id) ?? MAP_PRESETS[0];
}

/** Chernarus-style map grid reference, e.g. `075 123`, as shown by in-game maps. */
export function gridRef(x: number, z: number, mapSize: number): string {
  const gx = Math.floor(x / 100);
  const gz = Math.floor((mapSize - z) / 100);
  return `${String(Math.max(gx, 0)).padStart(3, '0')} ${String(Math.max(gz, 0)).padStart(3, '0')}`;
}

/* ------------------------------------------------------------------ */
/* Online tile layers                                                  */
/* ------------------------------------------------------------------ */

/**
 * A slippy-map tile pyramid (256 px tiles, `{z}/{x}/{y}`, y = 0 at the top).
 * Its bottom-left corner sits on world (0, 0) and zoom level 0 spans `extent`
 * metres, so level z has 2^z tiles of extent / 2^z metres each.
 */
export type TileSource = {
  id: string;
  label: string;
  mapId: string;
  url: string;
  extent: number;
  maxZoom: number;
  attribution: string;
};

/**
 * iZurvive renders Chernarus+ onto a pyramid slightly larger than the terrain:
 * 15360 m covers 0.9644165039 of it (the constant iZurvive's own SDK uses).
 * Checked by registering its tiles against Bohemia's official map.png - the
 * best fit is exactly this extent with zero offset.
 */
export const IZURVIVE_CHERNARUS_EXTENT = 15360 / 0.9644165039;

export const DEFAULT_IZURVIVE_VERSION = '1.25.0';

export function tileSources(izurviveVersion: string): TileSource[] {
  const v = izurviveVersion.trim() || DEFAULT_IZURVIVE_VERSION;
  return [
    {
      id: 'izurvive-sat',
      label: 'iZurvive satellite',
      mapId: 'chernarusplus',
      url: `https://maps.izurvive.com/maps/ChernarusPlus-Sat/${v}/tiles/{z}/{x}/{y}.jpg`,
      extent: IZURVIVE_CHERNARUS_EXTENT,
      maxZoom: 8,
      attribution: 'Tiles © iZurvive',
    },
    {
      id: 'izurvive-top',
      label: 'iZurvive topographic',
      mapId: 'chernarusplus',
      url: `https://maps.izurvive.com/maps/ChernarusPlus-Top/${v}/tiles/{z}/{x}/{y}.jpg`,
      extent: IZURVIVE_CHERNARUS_EXTENT,
      maxZoom: 8,
      attribution: 'Tiles © iZurvive',
    },
    {
      // xam.nu tiles span exactly the terrain (256 units = map size in its Leaflet CRS).
      id: 'xam-top',
      label: 'xam.nu topographic',
      mapId: 'chernarusplus',
      url: 'https://static.xam.nu/dayz/maps/chernarusplus/1.27/topographic/{z}/{x}/{y}.webp',
      extent: 15360,
      maxZoom: 7,
      attribution: 'Tiles © xam.nu',
    },
  ];
}

export type Tile = { z: number; x: number; y: number; url: string; x0: number; z0: number; size: number };

/**
 * Tiles covering the visible world rectangle at a zoom level that gives about
 * one tile pixel per screen pixel.
 */
export function visibleTiles(
  source: TileSource,
  scale: number,
  view: { x0: number; z0: number; x1: number; z1: number },
): Tile[] {
  const wanted = Math.ceil(Math.log2((scale * source.extent) / 256));
  const z = Math.min(Math.max(wanted, 0), source.maxZoom);
  const count = 2 ** z;
  const size = source.extent / count;
  const tx0 = Math.max(0, Math.floor(view.x0 / size));
  const tx1 = Math.min(count - 1, Math.floor(view.x1 / size));
  // Tile row 0 is the top (north) edge of the pyramid.
  const ty0 = Math.max(0, Math.floor((source.extent - view.z1) / size));
  const ty1 = Math.min(count - 1, Math.floor((source.extent - view.z0) / size));
  const tiles: Tile[] = [];
  for (let y = ty0; y <= ty1; y += 1) {
    for (let x = tx0; x <= tx1; x += 1) {
      tiles.push({
        z,
        x,
        y,
        url: source.url.replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y)),
        x0: x * size,
        z0: source.extent - (y + 1) * size,
        size,
      });
    }
  }
  return tiles;
}
